import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Select } from 'antd';

import RecordBadge from './RecordBadge';
import { optionSearchFilter } from '../Input';
import { api } from '../../api/client';
import { API_ITEMS_URL } from '../../utils';
import { getItemDisplayValue } from '../../utils/items';
import { normalizeId } from '../../utils/ids';
import { itemHomeCollectionId } from '../../utils/linkedRecords';
import { useOptionalCardDefinitions } from '../../contexts/CardDefinitionsContext';
import useEnsureCards from '../../hooks/useEnsureCards';

/**
 * An item picker for one card — badge, typeahead, a hex-id value, and a way to
 * keep saying what the free-text box it replaced could say.
 *
 * The automation builders needed the same thing InputCardLookup gives an item
 * form, minus everything that only makes sense while EDITING an item (quick
 * create, detail preview, view-all paging, multi-select). What is left is small
 * enough to state: fetch the target card's items, label each one with the
 * card's display template, and commit the item's id.
 *
 * THE PAGE IS A CAP, NOT THE CARD. The items endpoint answers 50 rows by
 * default and 200 at most, newest first, and the typeahead filters what
 * arrived — so on a card with 3000 residents, Molly is very likely not in the
 * list and no amount of typing will conjure her. That is why the free-text
 * escape below is not a nicety: the input this replaced let a manager type a
 * TITLE, and the API's compileLookup resolves a non-hex value through the
 * referenced item's title element. Removing that would have made a whole class
 * of condition unauthorable. So: a search that matches nothing offers to keep
 * the typed text as a title match, and a stored non-hex value renders as
 * itself, never as a failed id lookup.
 *
 * TWO fetches, both lazy and both narrow:
 *   1. the target card's items (descendants included — an item of a child card
 *      IS an item of its parent, the same rule the lookup input follows), and
 *   2. a batch `?ids=` resolve for a STORED hex id the list does not contain.
 * (2) is what makes a saved rule re-open readable: a condition stored as
 * `resident = 507f…011` must come back saying "Molly", not a hex string the
 * manager has no way to recognize — and the id may well point at an item
 * outside the page the list returned.
 *
 * WHY (2) waits for (1) to settle. The resolve used to list `items` as a
 * dependency, which meant the list's own arrival re-ran it: the cleanup
 * cancelled the in-flight `?ids=` response, and the effect then declined to
 * re-issue it because the id was already marked as attempted. The id resolved
 * to nothing, forever — precisely the case this component exists for, and
 * invisible to a test whose mocks both settle as microtasks before React
 * commits. Now the resolve starts only once the list has settled (so it also
 * knows whether it is needed at all), its cancellation is scoped to a genuine
 * change of card or value, and an id is marked attempted on COMPLETION rather
 * than on request — an abandoned request leaves nothing behind.
 *
 * Card definitions come from the OPTIONAL context: labels and the badge want
 * them, but this renders inside modals and tests with no provider, where it
 * degrades to the item's name rather than refusing to render.
 *
 * `multiple` is the same control with a list of values, for the filter
 * builder's `is any of` / `is none of` rows: same fetch, same title escape,
 * same stored-id resolve, an array in and an array out.
 *
 * THE BUILDERS CHECK WHAT IT HOLDS (ruling d21, utils/idValueValidation.js).
 * `onVocabulary` hands the builder what this picker knows — the items it has,
 * titled, and whether every stored id has been ANSWERED (`complete`) — so an
 * id nobody can find is flagged and a typed title offers the items that carry
 * it. The picker only reports; the builder judges and renders the flag, and
 * passes `status` back for the warning border. An id is "missing" only once a
 * `?ids=` response has come back without it: a request that failed or was
 * abandoned proves nothing, and leaves `complete` false.
 */

const HEX_ID_RE = /^[0-9a-f]{24}$/i;

export const looksLikeItemId = value => HEX_ID_RE.test( `${ value ?? '' }`.trim() );

/** The endpoint's own MAX_LIMIT (queryContract.js). Its default of 50 is not enough. */
export const ITEMS_LIMIT = 200;

/**
 * In-flight list requests, keyed `collectionId:cardId`. Two condition rows on
 * the same lookup mount in the same tick and would otherwise fire the same
 * query twice. Only the PENDING promise is shared — the entry is dropped once
 * it settles, so a row added later still sees items created since.
 */
const inFlightItems = new Map();

function fetchCardItems( collectionId, cardId ) {
	const key = `${ collectionId }:${ cardId }`;
	const pending = inFlightItems.get( key );
	if ( pending ) return pending;
	const request = api.get( API_ITEMS_URL, {
		params: {
			collection_id: collectionId,
			card_id: cardId,
			include_descendants: true,
			limit: ITEMS_LIMIT,
		},
	} )
		.then( res => ( Array.isArray( res?.data ) ? res.data : [] ) )
		// An empty list still leaves the row editable (the free-text escape
		// below covers it) and the API still validates the saved condition; a
		// throw here would take the whole builder down over one unreadable card.
		.catch( () => [] )
		.finally( () => { inFlightItems.delete( key ); } );
	inFlightItems.set( key, request );
	return request;
}

export default function ItemLookupSelect( {
	collectionId,
	cardId,
	value,
	onChange,
	disabled = false,
	allowClear = true,
	multiple = false,
	placeholder = 'Pick an item',
	ariaLabel,
	className,
	style,
	status,
	onVocabulary,
	// The title escape (see the header) is for a CONDITION, which resolves a
	// title at query time. A value a rule WRITES into a lookup takes records
	// only (keepr-api KPR-1104: text never fills a lookup), so the rule
	// editor's record picker turns it off: no typed title is offered, and the
	// value is always ids.
	titles = true,
} ) {

	const [ items, setItems ] = useState( [] );
	useEnsureCards( items ); // a descendant card added since the page loaded renders under its own template
	const [ listSettled, setListSettled ] = useState( false );
	const [ resolvedById, setResolvedById ] = useState( {} );
	const [ search, setSearch ] = useState( '' );
	const attemptedIds = useRef( new Set() );
	// Ids a `?ids=` response came back WITHOUT — the only proof an item is
	// gone (or unreadable). State, not a ref: the vocabulary reads it.
	const [ missingIds, setMissingIds ] = useState( () => new Set() );
	// Membership without a dependency: the resolve effect must not re-run (and
	// cancel itself) every time the list changes identity.
	const itemsRef = useRef( [] );

	const cardDefinitions = useOptionalCardDefinitions();
	const cardsById = cardDefinitions?.cardsById;
	const getElementsForCard = cardDefinitions?.getElementsForCard;

	const targetCardId = normalizeId( cardId ) || '';
	// The target card's OWN collection when another collection owns it — a
	// published global collection (docs/plans/personal-discover-global.md
	// §4.5) — so the picker lists that collection's records.
	const ensureTarget = useMemo( () => ( targetCardId ? [ targetCardId ] : [] ), [ targetCardId ] );
	useEnsureCards( ensureTarget );
	const targetDef = targetCardId ? cardsById?.[ targetCardId ] : null;
	const targetOwner = targetDef?.scope === 'collection' ? normalizeId( targetDef.collection_id ) : '';
	const sourceCollectionId = targetOwner && targetOwner !== normalizeId( collectionId ) ? targetOwner : collectionId;
	// One control, two arities. `in (…)` on a lookup is the same question asked
	// of several items, so the list operators get a multi-select rather than a
	// second component — and everything below (the title escape, the stored-id
	// resolve) works per value either way.
	const valueList = useMemo( () => (
		( multiple ? ( Array.isArray( value ) ? value : [ value ] ) : [ value ] )
			.map( entry => `${ entry ?? '' }`.trim() )
			.filter( Boolean )
	), [ multiple, value ] );
	const normalizedValue = multiple ? '' : ( valueList[ 0 ] || '' );
	// A stable dependency: the effect below must not re-run (and cancel its own
	// request) because an array was rebuilt with the same contents.
	const valueKey = valueList.join( ',' );

	useEffect( () => {
		attemptedIds.current = new Set();
		itemsRef.current = [];
		setItems( [] );
		setResolvedById( {} );
		setMissingIds( new Set() );
		setListSettled( false );
		if ( ! sourceCollectionId || ! targetCardId ) return undefined;
		let active = true;
		fetchCardItems( sourceCollectionId, targetCardId ).then( list => {
			if ( ! active ) return;
			itemsRef.current = list;
			setItems( list );
			setListSettled( true );
		} );
		return () => { active = false; };
	}, [ sourceCollectionId, targetCardId ] );

	useEffect( () => {
		// Only a hex id is resolvable. A non-hex stored value is a TITLE match
		// the API resolves at query time; asking the items endpoint for it would
		// be meaningless, and it renders as itself below.
		if ( ! listSettled ) return undefined;
		const wanted = valueKey.split( ',' ).filter( Boolean ).filter( entry => (
			looksLikeItemId( entry )
			&& ! attemptedIds.current.has( entry )
			&& ! itemsRef.current.some( item => normalizeId( item?._id ) === entry )
		) );
		if ( ! wanted.length ) return undefined;
		let active = true;
		const remember = () => {
			if ( active ) wanted.forEach( entry => attemptedIds.current.add( entry ) );
		};
		api.get( API_ITEMS_URL, { params: { ids: wanted.join( ',' ) } } ).then( res => {
			if ( ! active ) return;
			// Marked only now: an id abandoned mid-flight has not been asked
			// about as far as the next render is concerned, and must be asked
			// again rather than left showing a hex string.
			remember();
			const found = ( Array.isArray( res?.data ) ? res.data : [] )
				.filter( item => wanted.includes( normalizeId( item?._id ) ) );
			if ( found.length ) {
				setResolvedById( prev => {
					const next = { ...prev };
					found.forEach( item => { next[ normalizeId( item._id ) ] = item; } );
					return next;
				} );
			}
			// An answer that left an id out is the one proof it is gone.
			const gone = wanted.filter( entry => ! found.some( item => normalizeId( item._id ) === entry ) );
			if ( gone.length ) {
				setMissingIds( prev => {
					const next = new Set( prev );
					gone.forEach( entry => next.add( entry.toLowerCase() ) );
					return next;
				} );
			}
		} ).catch( remember );
		return () => { active = false; };
	}, [ collectionId, targetCardId, valueKey, listSettled ] );

	const labelFor = useCallback( item => {
		// Each row renders against its OWN card: a descendant carries its own
		// display template, which is what makes a child item read correctly.
		const itemCardId = normalizeId( item?.card_id ) || targetCardId;
		const card = cardsById?.[ itemCardId ] || cardsById?.[ targetCardId ] || null;
		const elementDefs = getElementsForCard ? ( getElementsForCard( itemCardId ) || [] ) : [];
		return {
			card,
			text: getItemDisplayValue( { card, elementDefs, item } ),
		};
	}, [ cardsById, getElementsForCard, targetCardId ] );

	const itemOptions = useMemo( () => {
		const pool = [ ...items ];
		// The resolved stored item is appended so the Select can render its
		// label; without an option carrying the value antd shows the raw id.
		Object.values( resolvedById ).forEach( item => {
			const id = normalizeId( item?._id );
			if ( id && ! pool.some( entry => normalizeId( entry?._id ) === id ) ) pool.push( item );
		} );
		return pool
			.map( item => {
				const id = normalizeId( item?._id );
				if ( ! id ) return null;
				const { card, text } = labelFor( item );
				return {
					value: id,
					label: (
						<span className='item-lookup-option'>
							{/* A record of ANOTHER collection than the list this
							    picker filters is its qualified badge. */}
							<RecordBadge kind='item' record={card} title={text} size='xs' collection={itemHomeCollectionId( item )} />
						</span>
					),
					// ReactNode label -> the typeahead reads this instead.
					searchText: text,
				};
			} )
			.filter( Boolean );
	}, [ items, resolvedById, labelFor ] );

	// What the builders check a value against (see the header): every item
	// this picker holds, titled, and whether each stored id has an answer.
	const vocabulary = useMemo( () => {
		const byId = new Map();
		[ ...items, ...Object.values( resolvedById ) ].forEach( item => {
			const id = normalizeId( item?._id );
			if ( ! id || byId.has( id ) ) return;
			const { card, text } = labelFor( item );
			// The card's name rides along: two items may share a title, and
			// a match offered by title has to say which is which.
			byId.set( id, { value: id, label: text, detail: card?.label?.singular || card?.name || undefined } );
		} );
		// Keyed on the joined string, never on `valueList`: a caller's list
		// value is a new array every render, and a new vocabulary each time
		// would report itself back to the builder in a loop.
		const storedIds = valueKey.split( ',' ).filter( looksLikeItemId ).map( entry => entry.toLowerCase() );
		const complete = listSettled && storedIds.every( id => byId.has( id ) || missingIds.has( id ) );
		return { items: [ ...byId.values() ], complete };
	}, [ items, resolvedById, labelFor, valueKey, listSettled, missingIds ] );

	// Reported when it CHANGES, through a ref: a caller's inline callback is
	// a new function every render and must not re-send the same vocabulary.
	const onVocabularyRef = useRef( onVocabulary );
	onVocabularyRef.current = onVocabulary;
	useEffect( () => {
		if ( typeof onVocabularyRef.current === 'function' ) onVocabularyRef.current( vocabulary );
	}, [ vocabulary ] );

	const titleOption = useCallback( ( text, saved ) => ( {
		value: text,
		label: (
			<span className='item-lookup-option'>
				<span className='item-lookup-option__title'>
					{saved ? text : `Use “${ text }”`}
				</span>
				<span className='item-lookup-option__hint'>matches by title</span>
			</span>
		),
		searchText: text,
	} ), [] );

	const options = useMemo( () => {
		const out = [ ...itemOptions ];
		const carries = candidate => out.some( option => option.value === candidate );

		// A SAVED non-hex value is a title condition somebody authored. It has to
		// render as itself or the Select shows a blank where their query is.
		if ( ! titles ) return out;
		valueList.forEach( entry => {
			if ( ! looksLikeItemId( entry ) && ! carries( entry ) ) out.push( titleOption( entry, true ) );
		} );

		// Nothing in the fetched page matches what they are typing — which on a
		// card bigger than one page means nothing, so offer the title match
		// rather than "No data" over a perfectly authorable condition.
		const typed = search.trim();
		if ( typed && ! carries( typed ) && ! out.some( option => optionSearchFilter( typed, option ) ) ) {
			out.push( titleOption( typed, false ) );
		}
		return out;
	}, [ itemOptions, valueList, search, titleOption, titles ] );

	return (
		<Select
			className={className}
			style={style}
			mode={multiple ? 'multiple' : undefined}
			showSearch
			allowClear={allowClear}
			disabled={disabled}
			status={status}
			loading={!! collectionId && !! targetCardId && ! listSettled}
			aria-label={ariaLabel}
			placeholder={listSettled ? placeholder : 'Loading items…'}
			filterOption={optionSearchFilter}
			searchValue={search}
			onSearch={setSearch}
			// The search box is cleared on close so a stale needle cannot keep
			// offering its title option after the user has moved on.
			onDropdownVisibleChange={open => { if ( ! open ) setSearch( '' ); }}
			value={multiple ? valueList : ( normalizedValue || undefined )}
			options={options}
			onChange={next => {
				setSearch( '' );
				onChange?.( multiple ? ( Array.isArray( next ) ? next : [] ) : ( next || '' ) );
			}}
		/>
	);
}
